<template>
  <v-data-table-server
    class="coronet-table--highlightable"
    :headers="headers"
    :items="items"
    :loading="loading"
    :page="pagination.page + 1"
    :items-length="totalItems"
    @update:options="handlePaginationUpdate"
    :items-per-page="pagination.pageSize"
  >
    <template #item="{ item }">
      <tr :key="item.id">
        <td class="w-33">
          <div class="d-flex align-center">
            <v-icon :icon="iconMap[item.exportFileExtension]" size="40" class="mr-2"></v-icon>
            <span class="subtitle2">{{ item.exportName }}</span>
          </div>
        </td>
        <td class="body2 w-33">
          {{ $t(`reports.exports.categories.${item.type}`) }}
        </td>
        <td class="body2 w-33">
          {{ getFormattedDateTime(item.creationTime) }}
        </td>
        <td class="actions-col">
          <div
            v-if="!actionNotAllowed"
            class="body2 coro-link"
            role="button"
            @click="downloadCsv(item)"
          >
            {{ $t(`reports.exports.exportFileExtensions.${item.exportFileExtension}`) }}
          </div>
        </td>
      </tr>
    </template>
  </v-data-table-server>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { Pagination } from "@/types.ts";
import { downloadFileByLink } from "@/_helpers/download";
import { getFormattedDateTime } from "@/_helpers/date";
import moment from "moment/moment";
import { ModalWidth } from "@/constants/modals.ts";
import ErrorModal from "@/components/modals/ErrorModal.vue";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { ExportFileExtension, type ExportListItem } from "@/constants/exports.ts";
import { dialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

const iconMap: Record<ExportFileExtension, string> = {
  [ExportFileExtension.CSV]: "$csvFile",
  [ExportFileExtension.ZIP]: "$zipFile",
};

export default defineComponent({
  props: {
    items: {
      type: Array as PropType<ExportListItem[]>,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    actionNotAllowed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const i18n = useI18n();
    const dialogStore = useDialogsStore();

    const handlePaginationUpdate = (options: { itemsPerPage: number; page: number }) => {
      emit("update:pagination", {
        pageSize: options.itemsPerPage,
        page: options.page - 1,
      });
    };

    const downloadCsv = (item: ExportListItem) => {
      const logCreatedHoursAgo = moment().diff(moment(item.creationTime), "hours");
      if (logCreatedHoursAgo > 24) {
        const errorModal = {
          width: ModalWidth.SMALL,
          header: undefined,
          content: {
            component: ErrorModal,
          },
          footer: null,
          data: {
            item: i18n.t("activityLogs.errors.csvDownloadLinkExpired"),
          },
        };
        const dialogConfig = dialogsConfigConstructor(errorModal);
        dialogStore.openDialog(dialogConfig);
        return;
      }
      downloadFileByLink(item.downloadLink);
    };

    return {
      headers: [
        {
          title: i18n.t("reports.exports.table.name"),
          key: "exportName",
          sortable: false,
        },
        {
          title: i18n.t("reports.exports.table.category"),
          key: "exportType",
          sortable: false,
        },
        {
          title: i18n.t("reports.exports.table.created"),
          key: "creationTime",
          sortable: false,
        },
        {
          title: "",
          key: "actions",
          sortable: false,
          headerProps: {
            "aria-label": i18n.t("ariaLabels.actionsColumn"),
          },
        },
      ],
      iconMap,
      getFormattedDateTime,
      downloadCsv,
      handlePaginationUpdate,
    };
  },
});
</script>

<style lang="scss" scoped>
.actions-col {
  width: 56px;
  text-align: center;
}
</style>
